import { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { t } from '@lingui/macro';

import { SEARCH_PAGE_Z_INDEX } from '@rover/constants/src/search/searchPage.constants';
import HomeEditModal from '@rover/react-lib/src/components/formFields/LocationInput/components/HomeEditModal';
import useHomeAddress from '@rover/react-lib/src/components/formFields/LocationInput/hooks/useHomeAddress';
import { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest';

import homeMarkerImage from './home-marker.png';
import HomeAddressCard from './HomeAddressCard';

type Props = {
  map?: google.maps.Map;
  google?: typeof google;
  onEdit?: (newAddressData: UserInformationAddress) => void;
};

const HomeAddressMarker = ({ map, google, onEdit }: Props): JSX.Element | null => {
  const { address, formattedAddress, enabled } = useHomeAddress();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);

  // Create container div for InfoWindow content
  const containerDiv = useMemo(() => {
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    return document.createElement('div');
  }, []);

  const shouldShowMarker = enabled && address && formattedAddress;

  useEffect(() => {
    if (!shouldShowMarker || !map || !google) {
      return undefined;
    }

    const position = {
      lat: Number(address.latitude),
      lng: Number(address.longitude),
    };

    const marker = new google.maps.Marker({
      position,
      map,
      title: t`Home`,
      zIndex: SEARCH_PAGE_Z_INDEX.HOME_ADDRESS_MARKER.toNumber(),
      icon: {
        url: homeMarkerImage,
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 24),
      },
    });

    const infoWindow = new google.maps.InfoWindow({
      content: containerDiv,
      disableAutoPan: false,
      headerDisabled: true,
    });

    const clickListener = google.maps.event.addListener(marker, 'click', () => {
      setIsInfoWindowOpen(true);
      infoWindow.open(map, marker);
    });

    const closeListener = google.maps.event.addListener(infoWindow, 'closeclick', () => {
      setIsInfoWindowOpen(false);
    });

    return () => {
      google.maps.event.removeListener(clickListener);
      google.maps.event.removeListener(closeListener);
      marker.setMap(null);
      setIsInfoWindowOpen(false);
    };
  }, [shouldShowMarker, map, google, address, containerDiv]);

  const handleCloseEditModal = (): void => {
    setIsEditModalOpen(false);
  };

  if (!shouldShowMarker) {
    return null;
  }

  return (
    <>
      {/* Portal HomeAddressCard into InfoWindow when open */}
      {isInfoWindowOpen &&
        ReactDOM.createPortal(
          <HomeAddressCard
            address={formattedAddress}
            onEditClick={() => setIsEditModalOpen(true)}
          />,
          containerDiv
        )}
      <HomeEditModal isOpen={isEditModalOpen} onClose={handleCloseEditModal} onEdit={onEdit} />
    </>
  );
};

export default HomeAddressMarker;
